import styled from 'styled-components';
import { up } from '@/themes';
import Button from './button';
import Reveal from './reveal';
import { sectionTitleStyle } from './typography';

const Panel = styled(Reveal)`
  background-image: var(--gradient-brand);
  border-radius: var(--radius-3xl);
  box-shadow: var(--shadow-lg);
  isolation: isolate;
  overflow: hidden;
  padding: 64px 24px;
  position: relative;
  text-align: center;

  ${up.sm} {
    padding-left: 64px;
    padding-right: 64px;
  }

  /* Soft highlight from the top edge. */
  &::before {
    background: radial-gradient(ellipse at top, rgb(255 255 255 / 0.18), transparent 60%);
    content: '';
    inset: 0;
    pointer-events: none;
    position: absolute;
    z-index: -1;
  }
`;

const Title = styled.h2`
  ${sectionTitleStyle};
  color: var(--primary-foreground);
  margin-left: auto;
  margin-right: auto;
  max-width: 672px;
`;

const Description = styled.p`
  color: color-mix(in oklab, var(--primary-foreground) 85%, transparent);
  font-size: 18px;
  line-height: 28px;
  margin: 16px auto 0;
  max-width: 576px;
`;

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  justify-content: center;
  margin-top: 32px;

  ${up.sm} {
    flex-direction: row;
  }
`;

const Extra = styled.div`
  margin-top: 32px;
`;

/**
 * Closing call to action on the brand gradient. The first action renders as
 * the white `inverse` button, the rest as `glass`.
 *
 *   <CtaPanel
 *     title="Find the right gift card"
 *     description="4,700+ brands, delivered by email."
 *     actions={[
 *       { label: 'Browse gift cards', href: '/buy' },
 *       { label: 'Contact us', href: '/contact' },
 *     ]}
 *   />
 *
 * An action is `{ label, href?, onClick?, variant?, ...buttonProps }`.
 * `children` render under the buttons (for example app store badges).
 */
const CtaPanel = ({ actions = [], as = 'h2', children, className, description, title }) => (
  <Panel className={className}>
    <Title as={as}>{title}</Title>
    {description && <Description>{description}</Description>}
    {actions.length > 0 && (
      <Actions>
        {actions.map(({ label, variant, ...action }, index) => (
          <Button
            key={`${action.href || index}-${label}`}
            $size="pill"
            $variant={variant || (index === 0 ? 'inverse' : 'glass')}
            {...action}
          >
            {label}
          </Button>
        ))}
      </Actions>
    )}
    {children && <Extra>{children}</Extra>}
  </Panel>
);

export default CtaPanel;
